<template>
  <div
    class="entry-cursor absolute pointer-events-none"
    :style="{
      visibility: dragging ? null : 'hidden',
      top: posY - deltaY + 'px',
      left: posX - deltaX + 'px'
    }"
    draggable="false"
  >
    <slot v-bind:entry="entry"></slot>
  </div>
</template>

<script>
import Vue from "vue";
import { ViewportHelper } from "@/helpers/viewport-helper";
import { Easings } from "@/helpers/easings";
import { uuid } from "vue-uuid";

/**
 * Listens to dragstart events on the dragBus.
 * Emits a drag event every frame the cursor is being dragged.
 * Emits a dragend event when the drag is released.
 * Emits a drop event if at least one drop interaction is suggested by dragend listeners.
 */
export default {
  name: "DraggableItemCursor",
  props: {
    dragBus: {
      type: Vue,
      required: true
    }
  },
  data: () => {
    return {
      uid: uuid.v4(),
      entry: null,
      source: null,
      dragging: false,
      isTouchInteraction: false,
      posX: 0,
      posY: 0,
      deltaX: 0,
      deltaY: 0
    };
  },
  mounted() {
    this.dragBus.$on("dragstart", this.onDragStart);
  },
  methods: {
    onDragStart(event) {
      this.entry = event.entry;
      this.source = event.source;

      this.isTouchInteraction = event.isTouchInteraction;
      this.posX = event.posX;
      this.posY = event.posY;
      this.deltaX = event.deltaX + event.initiationDeltaX;
      this.deltaY = event.deltaY + event.initiationDeltaY;
      this.hookEvents();
      setTimeout(() => {
        this.dragging = true;
        this.startInitiationLerper(
          event.deltaX,
          event.deltaY,
          event.initiationDeltaX,
          event.initiationDeltaY
        );
      }, 10);
      if (this.isTouchInteraction) {
        // noinspection JSUnresolvedVariable
        this.$root.$el.style.pointerEvents = "none";
      }
      const documentSelection = document.getSelection();
      if (documentSelection) {
        documentSelection.empty();
      }
    },
    onDragEnd() {
      // noinspection JSUnresolvedVariable
      this.$root.$el.style.pointerEvents = null;
      this.dragging = false;
      this.releaseHooks();

      const possibleTargets = [];
      this.dragBus.$emit("dragend", {
        entry: this.entry,
        source: this.source,
        clientX: this.posX,
        clientY: this.posY,
        x: this.posX - this.deltaX,
        y: this.posY - this.deltaY,
        targets: possibleTargets
      });
      if (possibleTargets.length > 0) {
        possibleTargets.sort((a, b) => {
          if (a.priority < b.priority) return 1;
          if (a.priority > b.priority) return -1;
          return 0;
        });
        this.dragBus.$emit("drop", {
          entry: this.entry,
          source: this.source,
          clientX: this.posX,
          clientY: this.posY,
          x: this.posX - this.deltaX,
          y: this.posY - this.deltaY,
          target: possibleTargets[0].target
        });
      }
    },
    onPointerMove(event) {
      const position = ViewportHelper.getViewportPosition(event);
      this.posX = position.x;
      this.posY = position.y;
      this.dragBus.$emit("drag", {
        entry: this.entry,
        source: this.source,
        clientX: position.x,
        clientY: position.y,
        x: position.x - this.deltaX,
        y: position.y - this.deltaY
      });
    },
    onPointerUp() {
      this.onDragEnd();
    },
    hookEvents() {
      if (this.isTouchInteraction) {
        document.body.addEventListener("touchmove", this.onPointerMove);
        document.body.addEventListener("touchend", this.onPointerUp);
        document.body.addEventListener("touchcancel", this.onPointerUp);
      } else {
        document.body.addEventListener("mousemove", this.onPointerMove);
        document.body.addEventListener("mouseup", this.onPointerUp);
      }
    },
    releaseHooks() {
      if (this.isTouchInteraction) {
        document.body.removeEventListener("touchmove", this.onPointerMove);
        document.body.removeEventListener("touchend", this.onPointerUp);
        document.body.removeEventListener("touchcancel", this.onPointerUp);
      } else {
        document.body.removeEventListener("mousemove", this.onPointerMove);
        document.body.removeEventListener("mouseup", this.onPointerUp);
      }
    },
    getFleetItemPosition(entryId) {
      const match = this.matches[entryId];
      return match && match.rank != null ? match.rank : null;
    },
    startInitiationLerper(deltaX, deltaY, initiationDeltaX, initiationDeltaY) {
      const initiationLerpTime = 0.1;
      let t = 0;
      const step = 1 / 50 / initiationLerpTime;
      const initiationLerper = () => {
        if (!this.dragging) {
          return;
        }
        setTimeout(() => {
          t = Math.min(1, t + step);
          const progress = 1 - Easings.easeInOutSine(t);
          this.deltaX = deltaX + progress * initiationDeltaX;
          this.deltaY = deltaY + progress * initiationDeltaY;
          if (t < 1) {
            initiationLerper();
          }
        }, 20);
      };
      initiationLerper();
    }
  }
};
</script>

<style scoped>
.entry-cursor {
  z-index: 9999;
  touch-action: none;
}
</style>
